/**
 * Canonical reverse-order lifecycle: cancel, refund, return/restock.
 * Provider adapters live at the boundary. Inventory and money stay independent.
 */
import { money, roundMoney } from "./providers/order-financials";
import { remainingFulfilmentQty, TERMINAL_STATUSES } from "./sales-orders";
import { providerDisplayName } from "./order-provenance";

export const REFUND_INVENTORY_NOTICE =
  "Refunding does not automatically return stock to inventory.";

export const REFUND_ONLY_COPY =
  "Refund the customer but keep this quantity on the order for fulfilment.";

export const REFUND_AND_CANCEL_COPY =
  "Refund the customer and remove this quantity from fulfilment. Reserved stock will be released.";

export const REFUND_AFTER_RETURN_COPY =
  "Return completed and item restocked. Refund is financial only. Refunding does not restock again.";

export const MARKETPLACE_STATUS_INDEPENDENCE_COPY =
  "Tradivo order lifecycle, payment, and the channel order status are independent. A confirmed channel refund does not require the provider order to be Cancelled or Returned.";

export const CHANNEL_CANCEL_REVERSAL_COPY =
  "The channel reversed the buyer payment as part of the cancellation. Tradivo has not issued a refund. Historical merchandise, shipping, and total are unchanged.";

export function channelCancellationConfirmedLabel(providerName: string): string {
  return `${providerDisplayName(providerName.trim() || "channel")} cancellation confirmed`;
}

export function hasCompletedSalesReturn(returns: Array<{ status?: string | null }>): boolean {
  return returns.some((row) => (row.status ?? "").toLowerCase() === "completed");
}

export type UnfulfilledRefundDisposition = "refund_only" | "refund_and_cancel_unfulfilled";

export function refundedUnfulfilledCancellableQty(opts: {
  qtyOrdered: number;
  qtyFulfilled: number;
  qtyCancelled: number;
  qtyRefunded: number;
  qtyAlreadyCancelledFromRefund: number;
}): number {
  const unfulfilled = remainingFulfilmentQty(opts.qtyOrdered, opts.qtyCancelled, opts.qtyFulfilled);
  const leftover = Math.max(
    0,
    Math.floor(Number(opts.qtyRefunded) || 0) - Math.max(0, Math.floor(Number(opts.qtyAlreadyCancelledFromRefund) || 0)),
  );
  return Math.max(0, Math.min(unfulfilled, leftover));
}

export function assertCancelRefundedUnfulfilledQty(opts: {
  qtyOrdered: number;
  qtyFulfilled: number;
  qtyCancelled: number;
  qtyRefunded: number;
  qtyAlreadyCancelledFromRefund: number;
  requestedQty: number;
}): { ok: true; qty: number } | { ok: false; code: string; message: string } {
  const requested = Math.floor(Number(opts.requestedQty) || 0);
  if (requested <= 0) {
    return { ok: false, code: "CANCEL_QTY_INVALID", message: "Cancel quantity must be positive." };
  }
  const unfulfilled = remainingFulfilmentQty(opts.qtyOrdered, opts.qtyCancelled, opts.qtyFulfilled);
  if (unfulfilled <= 0 && (opts.qtyFulfilled ?? 0) > 0) {
    return { ok: false, code: "REFUNDED_QTY_FULFILLED", message: "Cannot cancel refunded quantity that is already fulfilled." };
  }
  const eligible = refundedUnfulfilledCancellableQty(opts);
  if (requested > eligible) {
    return {
      ok: false,
      code: "CANCEL_QTY_EXCEEDS_ELIGIBLE",
      message: `Cannot cancel quantity ${requested}; only ${eligible} refunded unfulfilled unit(s) remain.`,
    };
  }
  return { ok: true, qty: requested };
}

export function applyCancelRefundedUnfulfilledInventory(opts: {
  onHand: number;
  allocated: number;
  releaseAllocatedQty: number;
}): { onHand: number; allocated: number; available: number } {
  const allocated = Math.max(0, opts.allocated - Math.max(0, opts.releaseAllocatedQty));
  return { onHand: opts.onHand, allocated, available: opts.onHand - allocated };
}

export function requiresUnfulfilledDisposition(unfulfilledRefundQty: number): boolean {
  return unfulfilledRefundQty > 0;
}

export function providerUnfulfilledCancelStaysLocal(): true {
  return true;
}

export const ORDER_CANCEL_COPY = {
  title: "Cancel remaining unfulfilled quantity",
  intro:
    "This cancels only remaining unfulfilled quantity. Already refunded or cancelled quantity is not cancelled twice.",
  qtyRemainingLabel: "Qty remaining",
  qtyToCancelLabel: "Qty to cancel",
  releasesAllocation: "Cancellation releases reserved/allocated stock.",
  noPhysicalStock: "Cancellation does not add physical stock.",
  noRefund: "Cancellation does not issue another refund.",
  noDouble: "Already refunded or cancelled quantity is not cancelled twice.",
  confirmLabel: "Cancel remaining quantity",
  reasonLabel: "Cancellation reason",
  reasonRequired: "Choose a cancellation reason before continuing.",
  localDoneProviderPending: "Tradivo cancelled this quantity. The marketplace cancellation is not confirmed yet.",
  sellerFaultWarning:
    "This reason is treated as seller-fault on the marketplace. Seller-performance consequences may apply.",
} as const;

export const EBAY_CANCEL_REASON_OPTIONS = [
  { code: "BUYER_ASKED_CANCEL", label: "Buyer asked to cancel", sellerFault: false },
  { code: "ADDRESS_ISSUES", label: "Issue with buyer's delivery address", sellerFault: false },
  { code: "OUT_OF_STOCK_OR_CANNOT_FULFILL", label: "Out of stock or damaged", sellerFault: true },
] as const;

export type EbayCancelReasonCode = (typeof EBAY_CANCEL_REASON_OPTIONS)[number]["code"];

export function isEbayCancelReasonCode(value: unknown): value is EbayCancelReasonCode {
  return EBAY_CANCEL_REASON_OPTIONS.some((row) => row.code === value);
}

export function ebayCancelReasonIsSellerFault(code: unknown): boolean {
  return EBAY_CANCEL_REASON_OPTIONS.some((row) => row.code === code && row.sellerFault);
}

export type RemainingCancelLinePreview = {
  salesOrderItemId: string;
  productName: string;
  options: string;
  sku: string;
  qtyRemaining: number;
  qtyToCancel: number;
  qtyAlreadyCancelled: number;
  qtyRefunded: number;
  qtyAllocated: number;
};

export function remainingCancelLinePreviews(
  items: Array<{
    id: string;
    productName?: string | null;
    options?: string | null;
    sku?: string | null;
    qtyOrdered: number;
    qtyCancelled: number;
    qtyFulfilled: number;
    qtyAllocated: number;
    qtyRefunded?: number;
  }>,
): RemainingCancelLinePreview[] {
  return items
    .map((it) => {
      const qtyRemaining = remainingFulfilmentQty(it.qtyOrdered, it.qtyCancelled, it.qtyFulfilled);
      return {
        salesOrderItemId: it.id,
        productName: (it.productName ?? "").trim() || "Product",
        options: (it.options ?? "").trim(),
        sku: (it.sku ?? "").trim(),
        qtyRemaining,
        qtyToCancel: qtyRemaining,
        qtyAlreadyCancelled: Math.max(0, Math.floor(Number(it.qtyCancelled) || 0)),
        qtyRefunded: Math.max(0, Math.floor(Number(it.qtyRefunded) || 0)),
        qtyAllocated: Math.max(0, Math.floor(Number(it.qtyAllocated) || 0)),
      };
    })
    .filter((line) => line.qtyToCancel > 0);
}

/**
 * Whole-provider-order cancel is unsafe when the remote order already has
 * confirmed refund history. Remaining unfulfilled qty is cancelled locally.
 */
export function remainingOrderCancelMustStayLocal(opts: {
  hasConfirmedProviderRefund: boolean;
  paymentStatus?: string | null;
}): boolean {
  if (opts.hasConfirmedProviderRefund) return true;
  const pay = (opts.paymentStatus ?? "").toLowerCase();
  return pay === "partially_refunded" || pay === "refunded";
}

export type ReverseAction = "cancel" | "refund" | "create_return";

export function refundableBalance(chargedTotal: unknown, alreadyRefunded: unknown): number {
  return roundMoney(Math.max(0, money(chargedTotal) - money(alreadyRefunded)));
}

export function composeRefundExposure(opts: {
  chargedTotal: unknown;
  alreadyRefunded: unknown;
  thisRefund?: unknown;
  taxInclusive?: boolean;
  tax?: unknown;
}): {
  charged: number;
  refunded: number;
  refundable: number;
  netRetained: number;
  thisRefund: number;
  originalTotalUnchanged: true;
} {
  const charged = money(opts.chargedTotal);
  const refunded = money(opts.alreadyRefunded);
  const thisRefund = money(opts.thisRefund);
  return {
    charged,
    refunded,
    refundable: refundableBalance(charged, refunded),
    netRetained: roundMoney(charged - refunded - thisRefund),
    thisRefund,
    originalTotalUnchanged: true,
  };
}

export function assertRefundAmountAllowed(opts: {
  chargedTotal: unknown;
  alreadyRefunded: unknown;
  amount: unknown;
}): { ok: true; amount: number } | { ok: false; code: string; message: string } {
  const amount = money(opts.amount);
  if (!(amount > 0)) {
    return { ok: false, code: "REFUND_AMOUNT_INVALID", message: "Refund amount must be positive." };
  }
  const refundable = refundableBalance(opts.chargedTotal, opts.alreadyRefunded);
  if (amount > refundable) {
    return {
      ok: false,
      code: "REFUND_EXCEEDS_BALANCE",
      message: `Refund amount ${amount} exceeds the refundable balance of ${refundable}.`,
    };
  }
  return { ok: true, amount };
}

export type RefundMode = "items" | "entire_order" | "order_level";

export type RefundLineState = {
  salesOrderItemId: string;
  productName?: string | null;
  sku?: string | null;
  variantLabel?: string | null;
  qtyOrdered: number;
  qtyAlreadyRefunded: number;
  lineCharged: unknown;
  amountAlreadyRefunded: unknown;
};

export type RefundQuoteLine = {
  salesOrderItemId: string;
  productName: string | null;
  sku: string | null;
  variantLabel: string | null;
  qtyOrdered: number;
  qtyAlreadyRefunded: number;
  qtyRefundable: number;
  qty: number;
  unitCharged: number;
  amount: number;
  taxComponent: number;
};

export type RefundQuote = {
  mode: RefundMode;
  merchandise: number;
  shipping: number;
  taxComponent: number;
  amount: number;
  lines: RefundQuoteLine[];
  originalTotalUnchanged: true;
  inventoryUnchanged: true;
  allocationUnchanged: true;
};

export function remainingRefundableQty(qtyOrdered: number, qtyAlreadyRefunded: number): number {
  return Math.max(0, Math.floor(Number(qtyOrdered) || 0) - Math.max(0, Math.floor(Number(qtyAlreadyRefunded) || 0)));
}

export function remainingLineAmount(lineCharged: unknown, amountAlreadyRefunded: unknown): number {
  return roundMoney(Math.max(0, money(lineCharged) - money(amountAlreadyRefunded)));
}

/**
 * Attribute a quantity refund to the remaining line authority.
 * Last remaining units absorb rounding remainder so qty 1 of 2 on A$1.98 is A$0.99,
 * and a later qty 1 closes the line at the leftover.
 */
export function amountForRefundQty(opts: {
  lineCharged: unknown;
  qtyOrdered: number;
  qtyAlreadyRefunded: number;
  amountAlreadyRefunded: unknown;
  qty: number;
}): { ok: true; amount: number; unitAmount: number; remainingQty: number } | { ok: false; code: string; message: string } {
  const remainingQty = remainingRefundableQty(opts.qtyOrdered, opts.qtyAlreadyRefunded);
  const remainingAmount = remainingLineAmount(opts.lineCharged, opts.amountAlreadyRefunded);
  const qty = Math.floor(Number(opts.qty));
  if (!Number.isFinite(qty) || qty <= 0) {
    return { ok: false, code: "REFUND_QTY_INVALID", message: "Refund quantity must be a positive integer." };
  }
  if (qty > remainingQty) {
    return {
      ok: false,
      code: "REFUND_QTY_EXCEEDS",
      message: `Quantity ${qty} exceeds the remaining refundable quantity of ${remainingQty}.`,
    };
  }
  const amount = qty === remainingQty ? remainingAmount : roundMoney((remainingAmount * qty) / remainingQty);
  if (amount <= 0) {
    return { ok: false, code: "REFUND_AMOUNT_INVALID", message: "Refund amount must be positive." };
  }
  return { ok: true, amount, unitAmount: roundMoney(amount / qty), remainingQty };
}

export function remainingShippingAmount(orderShipping: unknown, shippingAlreadyRefunded: unknown): number {
  return roundMoney(Math.max(0, money(orderShipping) - money(shippingAlreadyRefunded)));
}

/** Informational GST share. Never added on top of a tax-inclusive charged amount. */
export function allocateTaxComponent(opts: {
  refundAmount: unknown;
  chargedTotal: unknown;
  tax: unknown;
}): number {
  const amount = money(opts.refundAmount);
  const charged = money(opts.chargedTotal);
  const tax = money(opts.tax);
  if (!(amount > 0) || !(charged > 0) || !(tax > 0)) return 0;
  return roundMoney((amount * tax) / charged);
}

function lineDisplay(line: RefundLineState): Pick<RefundQuoteLine, "productName" | "sku" | "variantLabel" | "qtyOrdered" | "qtyAlreadyRefunded" | "qtyRefundable" | "unitCharged"> {
  const qtyRefundable = remainingRefundableQty(line.qtyOrdered, line.qtyAlreadyRefunded);
  const remaining = remainingLineAmount(line.lineCharged, line.amountAlreadyRefunded);
  return {
    productName: line.productName ?? null,
    sku: line.sku ?? null,
    variantLabel: line.variantLabel ?? null,
    qtyOrdered: line.qtyOrdered,
    qtyAlreadyRefunded: line.qtyAlreadyRefunded,
    qtyRefundable,
    unitCharged: qtyRefundable > 0 ? roundMoney(remaining / qtyRefundable) : money(line.lineCharged) && line.qtyOrdered > 0 ? roundMoney(money(line.lineCharged) / line.qtyOrdered) : 0,
  };
}

export function composeItemsRefund(opts: {
  chargedTotal: unknown;
  alreadyRefunded: unknown;
  tax?: unknown;
  shippingThisRefund?: unknown;
  shippingAlreadyRefunded?: unknown;
  orderShipping?: unknown;
  lines: RefundLineState[];
  selections: Array<{ salesOrderItemId: string; qty: number }>;
}): { ok: true; quote: RefundQuote } | { ok: false; code: string; message: string } {
  const quoteLines: RefundQuoteLine[] = [];
  let merchandise = 0;
  for (const sel of opts.selections) {
    const qty = Math.floor(Number(sel.qty) || 0);
    if (qty <= 0) continue;
    const line = opts.lines.find((l) => l.salesOrderItemId === sel.salesOrderItemId);
    if (!line) {
      return { ok: false, code: "REFUND_LINE_UNKNOWN", message: "Refund line does not belong to this order." };
    }
    const priced = amountForRefundQty({
      lineCharged: line.lineCharged,
      qtyOrdered: line.qtyOrdered,
      qtyAlreadyRefunded: line.qtyAlreadyRefunded,
      amountAlreadyRefunded: line.amountAlreadyRefunded,
      qty,
    });
    if (priced.ok === false) return priced;
    const display = lineDisplay(line);
    const taxComponent = allocateTaxComponent({ refundAmount: priced.amount, chargedTotal: opts.chargedTotal, tax: opts.tax });
    merchandise = roundMoney(merchandise + priced.amount);
    quoteLines.push({
      salesOrderItemId: line.salesOrderItemId,
      ...display,
      qty,
      amount: priced.amount,
      taxComponent,
    });
  }
  const shippingCap = remainingShippingAmount(opts.orderShipping, opts.shippingAlreadyRefunded);
  const shipping = roundMoney(Math.min(shippingCap, Math.max(0, money(opts.shippingThisRefund))));
  const amount = roundMoney(merchandise + shipping);
  const allowed = assertRefundAmountAllowed({ chargedTotal: opts.chargedTotal, alreadyRefunded: opts.alreadyRefunded, amount });
  if (allowed.ok === false) return allowed;
  if (quoteLines.length === 0 && shipping <= 0) {
    return { ok: false, code: "REFUND_QTY_INVALID", message: "Select a quantity to refund, or include a shipping adjustment." };
  }
  return {
    ok: true,
    quote: {
      mode: "items",
      merchandise,
      shipping,
      taxComponent: allocateTaxComponent({ refundAmount: amount, chargedTotal: opts.chargedTotal, tax: opts.tax }),
      amount,
      lines: quoteLines,
      originalTotalUnchanged: true,
      inventoryUnchanged: true,
      allocationUnchanged: true,
    },
  };
}

export function composeEntireOrderRefund(opts: {
  chargedTotal: unknown;
  alreadyRefunded: unknown;
  tax?: unknown;
  orderShipping?: unknown;
  shippingAlreadyRefunded?: unknown;
  lines: RefundLineState[];
}): { ok: true; quote: RefundQuote } | { ok: false; code: string; message: string } {
  const refundable = refundableBalance(opts.chargedTotal, opts.alreadyRefunded);
  const allowed = assertRefundAmountAllowed({ chargedTotal: opts.chargedTotal, alreadyRefunded: opts.alreadyRefunded, amount: refundable });
  if (allowed.ok === false) return allowed;
  const selections = opts.lines
    .map((l) => ({ salesOrderItemId: l.salesOrderItemId, qty: remainingRefundableQty(l.qtyOrdered, l.qtyAlreadyRefunded) }))
    .filter((s) => s.qty > 0);
  const items = composeItemsRefund({
    ...opts,
    shippingThisRefund: remainingShippingAmount(opts.orderShipping, opts.shippingAlreadyRefunded),
    selections,
  });
  if (items.ok === false) {
    return {
      ok: true,
      quote: {
        mode: "entire_order",
        merchandise: refundable,
        shipping: 0,
        taxComponent: allocateTaxComponent({ refundAmount: refundable, chargedTotal: opts.chargedTotal, tax: opts.tax }),
        amount: refundable,
        lines: [],
        originalTotalUnchanged: true,
        inventoryUnchanged: true,
        allocationUnchanged: true,
      },
    };
  }
  const merchAndShip = items.quote.amount;
  if (merchAndShip === refundable) {
    return { ok: true, quote: { ...items.quote, mode: "entire_order" } };
  }
  const scale = merchAndShip > 0 ? refundable / merchAndShip : 1;
  const lines = items.quote.lines.map((l, idx) => {
    const amount = idx === items.quote.lines.length - 1
      ? roundMoney(refundable - roundMoney(items.quote.lines.slice(0, -1).reduce((s, x) => s + roundMoney(x.amount * scale), 0) + roundMoney(items.quote.shipping * scale)))
      : roundMoney(l.amount * scale);
    return { ...l, amount, unitCharged: l.qty > 0 ? roundMoney(amount / l.qty) : l.unitCharged, taxComponent: allocateTaxComponent({ refundAmount: amount, chargedTotal: opts.chargedTotal, tax: opts.tax }) };
  });
  const shipping = roundMoney(items.quote.shipping * scale);
  const merchandise = roundMoney(refundable - shipping);
  return {
    ok: true,
    quote: {
      mode: "entire_order",
      merchandise,
      shipping,
      taxComponent: allocateTaxComponent({ refundAmount: refundable, chargedTotal: opts.chargedTotal, tax: opts.tax }),
      amount: refundable,
      lines,
      originalTotalUnchanged: true,
      inventoryUnchanged: true,
      allocationUnchanged: true,
    },
  };
}

export function composeOrderLevelRefund(opts: {
  chargedTotal: unknown;
  alreadyRefunded: unknown;
  amount: unknown;
  tax?: unknown;
}): { ok: true; quote: RefundQuote } | { ok: false; code: string; message: string } {
  const allowed = assertRefundAmountAllowed(opts);
  if (allowed.ok === false) return allowed;
  return {
    ok: true,
    quote: {
      mode: "order_level",
      merchandise: allowed.amount,
      shipping: 0,
      taxComponent: allocateTaxComponent({ refundAmount: allowed.amount, chargedTotal: opts.chargedTotal, tax: opts.tax }),
      amount: allowed.amount,
      lines: [],
      originalTotalUnchanged: true,
      inventoryUnchanged: true,
      allocationUnchanged: true,
    },
  };
}

export const HISTORICAL_REFUND_AUTHORITY_COPY =
  "Refund amounts come from this historical sales transaction, not the current Product Master or listing price.";

export const CURRENT_INVENTORY_VALUE_COPY =
  "Current inventory / Product Master value. This is not the amount the customer paid and is not refund authority.";

export const HISTORICAL_SALE_VALUE_COPY =
  "Historical merchandise the customer paid on the original order.";

export type CompensationMethod = "original_payment" | "store_credit";

export type ChannelCompensationCapabilities = {
  provider: string | null;
  methods: CompensationMethod[];
  storeCredit: "SUPPORTED" | "NOT_SUPPORTED" | "REQUIRES_DIFFERENT_PROVIDER_CONTRACT";
  storeCreditReason: string;
};

export function channelCompensationCapabilities(provider: string | null | undefined): ChannelCompensationCapabilities {
  const p = (provider ?? "").trim().toLowerCase();
  if (p === "ebay") {
    return {
      provider: "ebay",
      methods: ["original_payment"],
      storeCredit: "NOT_SUPPORTED",
      storeCreditReason: "eBay issue_refund returns money to the original payment method. Tradivo has no eBay store-credit compensation API.",
    };
  }
  if (p === "bigcommerce") {
    return {
      provider: "bigcommerce",
      methods: ["original_payment"],
      storeCredit: "REQUIRES_DIFFERENT_PROVIDER_CONTRACT",
      storeCreditReason:
        "Tradivo's BigCommerce refund adapter only posts v3 payment_actions refunds with PRODUCT, SHIPPING, or ORDER items. store_credit_amount on the v2 order is tender used at purchase, not a refund method. Customer store-credit APIs are not wired.",
    };
  }
  return {
    provider: p || null,
    methods: ["original_payment"],
    storeCredit: "NOT_SUPPORTED",
    storeCreditReason: "No store-credit compensation adapter is registered for this channel.",
  };
}

export function shippingAlreadyRefundedFromRows(
  refunds: Array<{ status?: string | null; shipping_amount?: unknown }>,
): number {
  return roundMoney(
    refunds
      .filter((r) => r.status !== "rejected" && r.status !== "failed")
      .reduce((s, r) => s + money(r.shipping_amount), 0),
  );
}

export function historicalRefundAuthority(opts: {
  chargedTotal: unknown;
  merchandiseCharged: unknown;
  shippingCharged: unknown;
  alreadyRefunded: unknown;
  shippingAlreadyRefunded: unknown;
  productMasterPrice?: unknown;
}): {
  chargedTotal: number;
  merchandiseCharged: number;
  shippingCharged: number;
  previouslyRefunded: number;
  previouslyRefundedShipping: number;
  remainingRefundable: number;
  remainingShipping: number;
  productMasterIsNotAuthority: true;
} {
  const chargedTotal = money(opts.chargedTotal);
  const merchandiseCharged = money(opts.merchandiseCharged);
  const shippingCharged = money(opts.shippingCharged);
  const previouslyRefunded = money(opts.alreadyRefunded);
  const previouslyRefundedShipping = money(opts.shippingAlreadyRefunded);
  return {
    chargedTotal,
    merchandiseCharged,
    shippingCharged,
    previouslyRefunded,
    previouslyRefundedShipping,
    remainingRefundable: refundableBalance(chargedTotal, previouslyRefunded),
    remainingShipping: remainingShippingAmount(shippingCharged, previouslyRefundedShipping),
    productMasterIsNotAuthority: true,
  };
}

export function defaultReturnBoundRefundQty(opts: {
  qtyReturned: number;
  qtyRefundable: number;
}): number {
  return Math.max(0, Math.min(Math.floor(Number(opts.qtyReturned) || 0), Math.floor(Number(opts.qtyRefundable) || 0)));
}

export function seedReturnBoundQty(
  lines: RefundLineState[],
  returnedQtyByLine: Record<string, number>,
): Record<string, number> {
  const next: Record<string, number> = {};
  for (const line of lines) {
    const refundable = remainingRefundableQty(line.qtyOrdered, line.qtyAlreadyRefunded);
    next[line.salesOrderItemId] = defaultReturnBoundRefundQty({
      qtyReturned: returnedQtyByLine[line.salesOrderItemId] ?? 0,
      qtyRefundable: refundable,
    });
  }
  return next;
}

export type CompensationQuote = RefundQuote & {
  adjustment: number;
  remainingRefundable: number;
  remainingAfterThis: number;
  originalCharged: number;
  previouslyRefunded: number;
  method: CompensationMethod;
};

export function composeCompensation(opts: {
  chargedTotal: unknown;
  alreadyRefunded: unknown;
  tax?: unknown;
  orderShipping?: unknown;
  shippingAlreadyRefunded?: unknown;
  lines: RefundLineState[];
  selections: Array<{ salesOrderItemId: string; qty: number }>;
  includeShipping: boolean;
  adjustmentAmount?: unknown;
  adjustmentReason?: string | null;
  method?: CompensationMethod;
  provider?: string | null;
  productMasterPrice?: unknown;
}): { ok: true; quote: CompensationQuote } | { ok: false; code: string; message: string } {
  void opts.productMasterPrice;
  const capabilities = channelCompensationCapabilities(opts.provider);
  const method = opts.method ?? "original_payment";
  if (!capabilities.methods.includes(method) || method === "store_credit") {
    return { ok: false, code: "COMPENSATION_METHOD_UNSUPPORTED", message: "Store credit is not available for this channel." };
  }
  const remainingShip = remainingShippingAmount(opts.orderShipping, opts.shippingAlreadyRefunded);
  const shippingThisRefund = opts.includeShipping ? remainingShip : 0;
  if (opts.includeShipping && remainingShip <= 0) {
    return { ok: false, code: "SHIPPING_NOT_REFUNDABLE", message: "No remaining refundable shipping on this historical order." };
  }
  const adjustment = roundMoney(Math.max(0, money(opts.adjustmentAmount)));
  if (adjustment > 0 && !(opts.adjustmentReason ?? "").trim()) {
    return { ok: false, code: "ADJUSTMENT_REASON_REQUIRED", message: "Order-level adjustments require a reason." };
  }
  const hasQty = opts.selections.some((s) => Math.floor(Number(s.qty) || 0) > 0);
  let merchandise = 0;
  let shipping = 0;
  let lines: RefundQuoteLine[] = [];
  let mode: RefundMode = "items";
  if (!hasQty && !opts.includeShipping && adjustment > 0) {
    mode = "order_level";
  } else {
    const items = composeItemsRefund({
      chargedTotal: opts.chargedTotal,
      alreadyRefunded: opts.alreadyRefunded,
      tax: opts.tax,
      orderShipping: opts.orderShipping,
      shippingAlreadyRefunded: opts.shippingAlreadyRefunded,
      shippingThisRefund,
      lines: opts.lines,
      selections: opts.selections,
    });
    if (items.ok === false) return items;
    merchandise = items.quote.merchandise;
    shipping = items.quote.shipping;
    lines = items.quote.lines;
    mode = items.quote.mode;
  }
  const amount = roundMoney(merchandise + shipping + adjustment);
  const allowed = assertRefundAmountAllowed({ chargedTotal: opts.chargedTotal, alreadyRefunded: opts.alreadyRefunded, amount });
  if (allowed.ok === false) return allowed;
  if (amount <= 0) {
    return { ok: false, code: "REFUND_AMOUNT_INVALID", message: "Select merchandise quantity, shipping, or a positive adjustment." };
  }
  const charged = money(opts.chargedTotal);
  const already = money(opts.alreadyRefunded);
  return {
    ok: true,
    quote: {
      mode,
      merchandise,
      shipping,
      adjustment,
      taxComponent: allocateTaxComponent({ refundAmount: amount, chargedTotal: opts.chargedTotal, tax: opts.tax }),
      amount,
      lines,
      originalTotalUnchanged: true,
      inventoryUnchanged: true,
      allocationUnchanged: true,
      remainingRefundable: refundableBalance(charged, already),
      remainingAfterThis: roundMoney(refundableBalance(charged, already) - amount),
      originalCharged: charged,
      previouslyRefunded: already,
      method,
    },
  };
}

export function refundQuoteIgnoresProductMaster(opts: {
  quoteMerchandise: unknown;
  historicalMerchandise: unknown;
  productMasterPrice: unknown;
}): boolean {
  const quote = money(opts.quoteMerchandise);
  const historical = money(opts.historicalMerchandise);
  const master = money(opts.productMasterPrice);
  return quote === historical && (master <= 0 || quote !== master);
}

export function paymentStatusAfterRefund(chargedTotal: unknown, refundedIncludingThis: unknown): "refunded" | "partially_refunded" {
  const charged = money(chargedTotal);
  const refunded = money(refundedIncludingThis);
  if (charged > 0 && refunded >= charged) return "refunded";
  return "partially_refunded";
}

export function canCancelSalesOrder(status: string | null | undefined, qtyFulfilled = 0): boolean {
  if (!status || TERMINAL_STATUSES.includes(status)) return false;
  if (status === "fulfilled" || status === "completed") return false;
  if (qtyFulfilled > 0 && status === "partially_fulfilled") return true;
  return true;
}

export function canRefundSalesOrder(opts: {
  paymentStatus?: string | null;
  chargedTotal: unknown;
  alreadyRefunded: unknown;
}): boolean {
  const pay = (opts.paymentStatus ?? "").toLowerCase();
  if (pay === "unpaid" || pay === "pending" || pay === "pending_payment") {
    if (money(opts.chargedTotal) <= 0) return false;
  }
  return refundableBalance(opts.chargedTotal, opts.alreadyRefunded) > 0;
}

export function canCreateSalesReturn(opts: {
  qtyFulfilled: number;
  qtyAlreadyReturned: number;
}): boolean {
  return Math.max(0, opts.qtyFulfilled - opts.qtyAlreadyReturned) > 0;
}

export function returnEligibleQty(qtyFulfilled: number, qtyCancelled: number, qtyAlreadyReturned: number): number {
  return Math.max(0, Math.max(0, qtyFulfilled - Math.max(0, qtyCancelled)) - Math.max(0, qtyAlreadyReturned));
}

export function applyCancelAllocation(state: { onHand: number; allocated: number }, qty: number): { onHand: number; allocated: number } {
  return {
    onHand: state.onHand,
    allocated: Math.max(0, state.allocated - Math.max(0, qty)),
  };
}

export function applyRefundInventory(state: { onHand: number; allocated: number }): { onHand: number; allocated: number } {
  return { onHand: state.onHand, allocated: state.allocated };
}

export function applyReturnRestock(state: { onHand: number; allocated: number }, qty: number, restock: boolean): { onHand: number; allocated: number } {
  if (!restock) return { onHand: state.onHand, allocated: state.allocated };
  return { onHand: state.onHand + Math.max(0, qty), allocated: state.allocated };
}

export const RETURN_PHYSICAL_NOTICE =
  "Return records physical goods coming back. It does not issue a refund.";

export const DO_NOT_RESTOCK_COPY =
  "Record returned goods without increasing sellable inventory.";

export const RETURN_REFUND_OPTIONAL_COPY =
  "Issue refund is optional and financially separate. Finalizing a return does not issue a refund.";

export function restockFinalizeCopy(locationName: string): string {
  const name = locationName.trim() || "the receiving location";
  return `Return this quantity to sellable inventory at ${name}.`;
}

/** Resellable condition never implies restock; the operator must choose. */
export function defaultFinalizeDispositionFromCondition(_condition: string | null | undefined): "" {
  return "";
}

export function mapFinalizeDispositionToRpc(choice: string): "restock" | "scrap" | null {
  if (choice === "restock") return "restock";
  if (choice === "do_not_restock") return "scrap";
  return null;
}

export function suggestedReceivingLocationId(opts: {
  fulfilmentLocationId: string | null | undefined;
  orgLocationIds: string[];
}): string {
  const id = (opts.fulfilmentLocationId ?? "").trim();
  if (id && opts.orgLocationIds.includes(id)) return id;
  return "";
}

export function clampReturnQty(requested: number, maxReturnable: number): number {
  const max = Math.max(0, Math.floor(Number(maxReturnable) || 0));
  if (max <= 0) return 0;
  const n = Math.floor(Number(requested) || 0);
  if (n < 1) return 1;
  return Math.min(n, max);
}

export function assertRestockLocation(opts: {
  orgId: string;
  locationOrgId: string | null | undefined;
  locationId: string | null | undefined;
  restock: boolean;
}): { ok: true } | { ok: false; code: string; message: string } {
  if (!opts.restock) return { ok: true };
  const loc = (opts.locationId ?? "").trim();
  if (!loc) {
    return { ok: false, code: "RESTOCK_LOCATION_REQUIRED", message: "Restock requires a Tradivo receiving location." };
  }
  if (!opts.locationOrgId || opts.locationOrgId !== opts.orgId) {
    return { ok: false, code: "RESTOCK_LOCATION_ORG", message: "Receiving location must belong to this organisation." };
  }
  return { ok: true };
}

export function planBcOrderCancel(opts: {
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
}): { ok: true; method: "PUT"; path: string; body: { status_id: number }; externalOrderId: number } | { ok: false; code: string; message: string } {
  const bound = Number(opts.boundExternalOrderId);
  if (!Number.isInteger(bound) || bound <= 0) {
    return { ok: false, code: "CANCEL_UNBOUND", message: "Provider cancel requires a bound BigCommerce order id." };
  }
  const requested = opts.requestedExternalOrderId == null ? bound : Number(opts.requestedExternalOrderId);
  if (requested !== bound) {
    return { ok: false, code: "CANCEL_ID_MISMATCH", message: "Cancel may only target the bound remote order id." };
  }
  return {
    ok: true,
    method: "PUT",
    path: `/v2/orders/${bound}`,
    body: { status_id: 5 },
    externalOrderId: bound,
  };
}

export function planBcRefundCreate(opts: {
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
  amount: unknown;
}): { ok: true; method: "POST"; path: string; getPath: string; externalOrderId: number; amount: number } | { ok: false; code: string; message: string } {
  const bound = Number(opts.boundExternalOrderId);
  if (!Number.isInteger(bound) || bound <= 0) {
    return { ok: false, code: "REFUND_UNBOUND", message: "Provider refund requires a bound BigCommerce order id." };
  }
  const requested = opts.requestedExternalOrderId == null ? bound : Number(opts.requestedExternalOrderId);
  if (requested !== bound) {
    return { ok: false, code: "REFUND_ID_MISMATCH", message: "Refund may only target the bound remote order id." };
  }
  const amount = money(opts.amount);
  if (!(amount > 0)) {
    return { ok: false, code: "REFUND_AMOUNT_INVALID", message: "Refund amount must be positive." };
  }
  return {
    ok: true,
    method: "POST",
    path: `/v3/orders/${bound}/payment_actions/refunds`,
    getPath: `/v3/orders/${bound}/payment_actions/refunds`,
    externalOrderId: bound,
    amount,
  };
}

export type BcRefundItem =
  | { item_type: "PRODUCT"; item_id: number; quantity: number }
  | { item_type: "SHIPPING"; item_id: number; amount: number }
  | { item_type: "ORDER"; item_id: number; amount: number };

export function planBcRefundPayload(opts: {
  boundExternalOrderId?: unknown;
  mode: RefundMode;
  products?: Array<{ orderProductId: unknown; qty: unknown; sku?: string | null }>;
  shipping?: { orderAddressId: unknown; amount: unknown } | null;
  orderLevelAmount?: unknown;
}): { ok: true; quotePath: string; refundPath: string; getPath: string; items: BcRefundItem[] } | { ok: false; code: string; message: string } {
  const bound = Number(opts.boundExternalOrderId);
  if (!Number.isInteger(bound) || bound <= 0) {
    return { ok: false, code: "REFUND_UNBOUND", message: "Provider refund requires a bound BigCommerce order id." };
  }
  const quotePath = `/v3/orders/${bound}/payment_actions/refund_quotes`;
  const refundPath = `/v3/orders/${bound}/payment_actions/refunds`;
  const getPath = `/v3/orders/${bound}/payment_actions/refunds`;
  if (opts.mode === "order_level") {
    const amount = money(opts.orderLevelAmount);
    if (!(amount > 0)) return { ok: false, code: "REFUND_AMOUNT_INVALID", message: "Refund amount must be positive." };
    return { ok: true, quotePath, refundPath, getPath, items: [{ item_type: "ORDER", item_id: bound, amount }] };
  }
  const items: BcRefundItem[] = [];
  for (const p of opts.products ?? []) {
    const id = Number(p.orderProductId);
    const qty = Math.floor(Number(p.qty) || 0);
    if (!Number.isInteger(id) || id <= 0) {
      return { ok: false, code: "REFUND_LINE_UNMAPPED", message: `Could not map SKU ${p.sku ?? ""} to a BigCommerce order product.` };
    }
    if (qty <= 0) continue;
    items.push({ item_type: "PRODUCT", item_id: id, quantity: qty });
  }
  const shipAmt = money(opts.shipping?.amount);
  if (shipAmt > 0) {
    const addr = Number(opts.shipping?.orderAddressId);
    if (!Number.isInteger(addr) || addr <= 0) {
      return { ok: false, code: "REFUND_SHIPPING_UNMAPPED", message: "Shipping refund requires a BigCommerce order address id." };
    }
    items.push({ item_type: "SHIPPING", item_id: addr, amount: shipAmt });
  }
  if (items.length === 0) {
    return { ok: false, code: "REFUND_ITEMS_EMPTY", message: "BigCommerce item refund requires a product quantity or shipping amount." };
  }
  return { ok: true, quotePath, refundPath, getPath, items };
}

export function mapSkuToBcOrderProductId(
  sku: string | null | undefined,
  products: Array<{ id: unknown; sku?: unknown }>,
): number | null {
  const key = (sku ?? "").trim().toLowerCase();
  if (!key) return null;
  const matches = products.filter((p) => String(p.sku ?? "").trim().toLowerCase() === key);
  if (matches.length !== 1) return null;
  const id = Number(matches[0].id);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function parseBcRefundList(payload: unknown): Array<{ id: string; totalAmount: number; items: Array<{ itemType: string; quantity: number; amount: number }> }> {
  const root = payload && typeof payload === "object" ? (payload as { data?: unknown }) : null;
  const rows = Array.isArray(payload) ? payload : Array.isArray(root?.data) ? root.data : [];
  return rows.map((row) => {
    const rec = (row ?? {}) as { id?: unknown; total_amount?: unknown; items?: Array<{ item_type?: unknown; quantity?: unknown; requested_amount?: unknown; amount?: unknown }> };
    return {
      id: String(rec.id ?? ""),
      totalAmount: money(rec.total_amount),
      items: (rec.items ?? []).map((it) => ({
        itemType: String(it.item_type ?? ""),
        quantity: Math.floor(Number(it.quantity) || 0),
        amount: money(it.requested_amount ?? it.amount),
      })),
    };
  }).filter((r) => r.id);
}

export function matchExistingBcRefund(opts: {
  refunds: unknown;
  amount: unknown;
  qty?: unknown;
}): { ok: true; providerRefundId: string } | { ok: false } {
  const amount = money(opts.amount);
  const qty = opts.qty == null ? null : Math.floor(Number(opts.qty));
  for (const row of parseBcRefundList(opts.refunds)) {
    if (row.totalAmount !== amount) continue;
    if (qty != null && qty > 0) {
      const productQty = row.items.filter((it) => it.itemType === "PRODUCT").reduce((s, it) => s + it.quantity, 0);
      if (productQty > 0 && productQty !== qty) continue;
    }
    return { ok: true, providerRefundId: row.id };
  }
  return { ok: false };
}

export function providerRefundWriteMustBeSkipped(existing: { ok: boolean; providerRefundId?: string }): boolean {
  return existing.ok === true && Boolean(existing.providerRefundId);
}

export function parseEbayOrderRefunds(payload: unknown): Array<{ id: string; status: string; amount: number; currency: string }> {
  const root = payload && typeof payload === "object" ? payload as { paymentSummary?: { refunds?: unknown } } : null;
  const rows = Array.isArray(root?.paymentSummary?.refunds) ? root.paymentSummary.refunds : [];
  return rows.map((row) => {
    const rec = (row ?? {}) as { refundId?: unknown; refundStatus?: unknown; amount?: { value?: unknown; currency?: unknown } };
    return {
      id: String(rec.refundId ?? ""),
      status: String(rec.refundStatus ?? "").toUpperCase(),
      amount: money(rec.amount?.value),
      currency: String(rec.amount?.currency ?? "").toUpperCase(),
    };
  }).filter((r) => r.id || r.amount > 0);
}

export function ebayProviderRefundIsAuthoritative(status: string | null | undefined): boolean {
  const s = String(status ?? "").toUpperCase();
  return s === "REFUNDED" || s === "SUCCESS";
}

export function matchExistingEbayRefund(opts: {
  order: unknown;
  amount: unknown;
  currency?: string | null;
}): { ok: true; providerRefundId: string; status: string; amount: number } | { ok: false } {
  const amount = money(opts.amount);
  const currency = String(opts.currency ?? "AUD").trim().toUpperCase() || "AUD";
  for (const row of parseEbayOrderRefunds(opts.order)) {
    if (row.amount !== amount) continue;
    if (row.currency && row.currency !== currency) continue;
    if (!ebayProviderRefundIsAuthoritative(row.status) && row.status !== "PENDING") continue;
    if (!row.id && !ebayProviderRefundIsAuthoritative(row.status)) continue;
    return { ok: true, providerRefundId: row.id || `amount:${row.amount}`, status: row.status, amount: row.amount };
  }
  const root = opts.order && typeof opts.order === "object"
    ? opts.order as { orderPaymentStatus?: unknown }
    : null;
  const payStatus = String(root?.orderPaymentStatus ?? "").toUpperCase();
  const rows = parseEbayOrderRefunds(opts.order);
  if (payStatus === "FULLY_REFUNDED" && rows.length === 1) {
    const row = rows[0];
    if (ebayProviderRefundIsAuthoritative(row.status) || row.status === "PENDING") {
      return { ok: true, providerRefundId: row.id || `amount:${row.amount}`, status: row.status, amount: row.amount };
    }
  }
  return { ok: false };
}

export function mapSkuToEbayLineItemId(
  sku: string | null | undefined,
  lines: Array<{ sku?: unknown; lineItemId?: unknown; line_item_id?: unknown; title?: unknown }>,
): string | null {
  const key = (sku ?? "").trim().toLowerCase();
  if (!key) return null;
  const matches = lines.filter((l) => String(l.sku ?? "").trim().toLowerCase() === key);
  if (matches.length !== 1) return null;
  const id = String(matches[0].lineItemId ?? matches[0].line_item_id ?? "").trim();
  return id || null;
}

export function planEbayCompensationRefundPayload(opts: {
  boundExternalOrderId?: unknown;
  connectorName?: string | null;
  currency?: string | null;
  totalAmount: unknown;
  merchandiseItems: Array<{ sku?: string | null; qty: unknown; amount: unknown }>;
  shippingAmount?: unknown;
  mappingLineItems?: Array<{ sku?: unknown; line_item_id?: unknown; lineItemId?: unknown }>;
}): { ok: true; method: "POST"; path: string; getPath: string; body: Record<string, unknown> } | { ok: false; code: string; message: string } {
  const planned = planEbayIssueRefund({
    boundExternalOrderId: opts.boundExternalOrderId,
    connectorName: opts.connectorName,
    amount: opts.totalAmount,
  });
  if (planned.ok === false) return planned;
  const currency = (opts.currency || "AUD").toString().trim().toUpperCase() || "AUD";
  const shipping = remainingShippingAmount(opts.shippingAmount, 0);
  const refundItems: Array<Record<string, unknown>> = [];
  let merchandise = 0;
  for (const it of opts.merchandiseItems) {
    const qty = Math.floor(Number(it.qty) || 0);
    const value = money(it.amount);
    if (qty <= 0 || !(value > 0)) continue;
    const lineItemId = mapSkuToEbayLineItemId(it.sku, opts.mappingLineItems ?? []);
    if (!lineItemId) {
      return { ok: false, code: "REFUND_LINE_UNMAPPED", message: `Could not map SKU ${it.sku ?? ""} to an eBay line item.` };
    }
    merchandise = roundMoney(merchandise + value);
    refundItems.push({
      lineItemId,
      refundQuantity: qty,
      refundAmount: { value: value.toFixed(2), currency },
    });
  }
  const amount = roundMoney(merchandise + shipping);
  if (amount !== planned.amount) {
    return { ok: false, code: "REFUND_AMOUNT_MISMATCH", message: "eBay refund payload must equal the canonical refund total." };
  }
  if (amount <= 0) {
    return { ok: false, code: "REFUND_AMOUNT_INVALID", message: "Refund amount must be positive." };
  }
  const body: Record<string, unknown> = {
    reasonForRefund: "RETURN",
    comment: "Tradivo historical refund",
  };
  if (refundItems.length > 0) body.refundItems = refundItems;
  if (shipping > 0) body.orderLevelRefundAmount = { value: shipping.toFixed(2), currency };
  if (refundItems.length === 0 && shipping <= 0) {
    return { ok: false, code: "REFUND_ITEMS_EMPTY", message: "eBay refund requires merchandise or shipping." };
  }
  return { ok: true, method: "POST", path: planned.path, getPath: planned.getPath, body };
}

export function planEbayOrderCancel(opts: {
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
  cancelReason?: unknown;
}): { ok: true; method: "POST"; path: string; eligibilityPath: string; getPath: string; externalOrderId: string; body: { legacyOrderId: string; cancelReason: EbayCancelReasonCode } } | { ok: false; code: string; message: string } {
  const bound = String(opts.boundExternalOrderId ?? "").trim();
  if (!bound || /[?&/]/.test(bound)) {
    return { ok: false, code: "CANCEL_UNBOUND", message: "Provider cancel requires a bound eBay order id." };
  }
  const requested = String(opts.requestedExternalOrderId ?? bound).trim();
  if (requested !== bound) {
    return { ok: false, code: "CANCEL_ID_MISMATCH", message: "Cancel may only target the bound remote order id." };
  }
  if (!isEbayCancelReasonCode(opts.cancelReason)) {
    return { ok: false, code: "CANCEL_REASON_REQUIRED", message: "eBay cancel requires an authoritative cancelReason." };
  }
  return {
    ok: true,
    method: "POST",
    path: `/post-order/v2/cancellation`,
    eligibilityPath: `/post-order/v2/cancellation/check_eligibility`,
    getPath: `/sell/fulfillment/v1/order/${encodeURIComponent(bound)}`,
    externalOrderId: bound,
    body: { legacyOrderId: bound, cancelReason: opts.cancelReason },
  };
}

export function ebayCancelStateIsConfirmed(cancelState: unknown): boolean {
  const state = String(cancelState ?? "").toUpperCase();
  return state === "CANCELLED" || state === "CANCELED" || state === "CANCEL_CLOSED" || state.endsWith("_CANCELLED") || state.endsWith("_CANCELED");
}

export function ebayCancelMutationAlreadySettled(cancelState: unknown): boolean {
  const state = String(cancelState ?? "").toUpperCase();
  return (
    ebayCancelStateIsConfirmed(state) ||
    state === "CANCEL_PENDING" ||
    state === "CANCEL_REQUESTED" ||
    state === "CANCELLATION_PENDING" ||
    state === "IN_PROGRESS"
  );
}

const CONFIRMED_CANCEL_REQUEST_STATES = new Set([
  "COMPLETED",
  "CLOSED",
  "APPROVED",
  "ACCEPTED",
  "SUCCESS",
  "CANCELLED",
]);

const PENDING_CANCEL_REQUEST_STATES = new Set([
  "REQUESTED",
  "PENDING",
  "IN_PROGRESS",
  "OPEN",
  "WAITING",
  "WAITING_SELLER",
  "WAITING_BUYER",
]);

const REJECTED_CANCEL_REQUEST_STATES = new Set([
  "REJECTED",
  "FAILED",
  "DENIED",
  "DECLINED",
  "ERROR",
  "CANCELLED_REJECTED",
  "CLOSED_FAILURE",
]);

export function classifyEbayCancelReadback(opts: {
  cancelState?: unknown;
  cancelRequestState?: unknown;
}): "none" | "pending" | "confirmed" | "error" {
  const req = String(opts.cancelRequestState ?? "").toUpperCase();
  const state = String(opts.cancelState ?? "").toUpperCase();
  if (REJECTED_CANCEL_REQUEST_STATES.has(req) || REJECTED_CANCEL_REQUEST_STATES.has(state)) return "error";
  if (ebayCancelStateIsConfirmed(opts.cancelState) || CONFIRMED_CANCEL_REQUEST_STATES.has(req)) return "confirmed";
  if (ebayCancelMutationAlreadySettled(opts.cancelState) || PENDING_CANCEL_REQUEST_STATES.has(req)) return "pending";
  return "none";
}

export function shouldPostEbayCancellation(opts: {
  cancelState?: unknown;
  eligibleToCancel?: unknown;
  orderPaymentStatus?: unknown;
  cancelRequestState?: unknown;
}): boolean {
  if (classifyEbayCancelReadback({ cancelState: opts.cancelState, cancelRequestState: opts.cancelRequestState }) !== "none") {
    return false;
  }
  const pay = String(opts.orderPaymentStatus ?? "").toUpperCase();
  if (pay === "FULLY_REFUNDED" || pay === "PARTIALLY_REFUNDED") return false;
  return opts.eligibleToCancel === true;
}

export function planEbayIssueRefund(opts: {
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
  amount: unknown;
  connectorName?: string | null;
}): { ok: true; method: "POST"; path: string; getPath: string; externalOrderId: string; amount: number } | { ok: false; code: string; message: string } {
  if ((opts.connectorName ?? "").trim() === "Tradivo eBay Account B") {
    return { ok: false, code: "EBAY_ACCOUNT_B_ISOLATED", message: "Account B must remain isolated." };
  }
  const bound = String(opts.boundExternalOrderId ?? "").trim();
  if (!bound || /[?&/]/.test(bound)) {
    return { ok: false, code: "REFUND_UNBOUND", message: "Provider refund requires a bound eBay order id." };
  }
  const requested = String(opts.requestedExternalOrderId ?? bound).trim();
  if (requested !== bound) {
    return { ok: false, code: "REFUND_ID_MISMATCH", message: "Refund may only target the bound remote order id." };
  }
  const amount = money(opts.amount);
  if (!(amount > 0)) {
    return { ok: false, code: "REFUND_AMOUNT_INVALID", message: "Refund amount must be positive." };
  }
  return {
    ok: true,
    method: "POST",
    path: `/sell/fulfillment/v1/order/${encodeURIComponent(bound)}/issue_refund`,
    getPath: `/sell/fulfillment/v1/order/${encodeURIComponent(bound)}`,
    externalOrderId: bound,
    amount,
  };
}

export function planEbayRefundPayload(opts: {
  boundExternalOrderId?: unknown;
  requestedExternalOrderId?: unknown;
  connectorName?: string | null;
  mode: RefundMode;
  amount: unknown;
  currency?: string | null;
  items?: Array<{ lineItemId?: unknown; qty?: unknown; amount?: unknown }>;
}): { ok: true; method: "POST"; path: string; getPath: string; body: Record<string, unknown> } | { ok: false; code: string; message: string } {
  const planned = planEbayIssueRefund(opts);
  if (planned.ok === false) return planned;
  const currency = (opts.currency || "AUD").toString().trim().toUpperCase() || "AUD";
  if (opts.mode === "items") {
    const refundItems = (opts.items ?? [])
      .map((it) => {
        const lineItemId = String(it.lineItemId ?? "").trim();
        const qty = Math.floor(Number(it.qty) || 0);
        const value = money(it.amount);
        return { lineItemId, qty, value };
      })
      .filter((it) => it.lineItemId && it.qty > 0 && it.value > 0);
    if (refundItems.length === 0) {
      return { ok: false, code: "REFUND_LINE_UNMAPPED", message: "eBay item refund requires bound line item ids." };
    }
    return {
      ok: true,
      method: "POST",
      path: planned.path,
      getPath: planned.getPath,
      body: {
        reasonForRefund: "OTHER",
        comment: "Tradivo item refund",
        refundItems: refundItems.map((it) => ({
          lineItemId: it.lineItemId,
          refundQuantity: it.qty,
          refundAmount: { value: String(it.value), currency },
        })),
      },
    };
  }
  return {
    ok: true,
    method: "POST",
    path: planned.path,
    getPath: planned.getPath,
    body: {
      reasonForRefund: "OTHER",
      comment: opts.mode === "entire_order" ? "Tradivo entire-order refund" : "Tradivo order-level refund",
      refundItems: [],
      orderLevelRefundAmount: { value: String(planned.amount), currency },
    },
  };
}

export function marketplaceRefundMustUseProviderReadback(source: string | null | undefined): boolean {
  const s = (source ?? "").toLowerCase();
  return s === "bigcommerce" || s === "ebay";
}

export function refundRetryIsIdempotent(existing: { idempotency_key?: string | null; id: string } | null, key: string): string | null {
  if (existing && existing.idempotency_key === key) return existing.id;
  return null;
}
